import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useInteractionMetadata } from "../../../hooks/useInteraction";
import { useTranscript } from "../../../hooks/useTranscription";
import { useGetSummary } from "../../../hooks/useSummary";
import { useHostedTranscribeAndSummarizeFlow } from "../../hooks/useHostedTranscribeAndSummarizeFlow";
import { usePopoutManager } from "../../hooks/usePopoutManager";
import type { TabId } from "../DetailPane/DetailTabs";
import DetailTabContent from "../DetailPane/DetailTabContent";
import MiniPlaybackControls from "./MiniPlaybackControls";
import ClosePromptDialog from "./ClosePromptDialog";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Interaction types that have audio/video media. */
const MEDIA_TYPES = new Set(["Audio", "Video", "AudioAndVideo"]);

const TAB_LABELS: Record<string, string> = {
  transcript: "Transcript",
  summary: "Summary",
  metadata: "Metadata",
};

function getTabLabel(tabId: string): string {
  return TAB_LABELS[tabId] ?? tabId;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DetachedTabPage() {
  const {
    interactionId = "",
    customerId = "",
    tabId = "transcript",
  } = useParams<{
    interactionId: string;
    customerId: string;
    tabId: string;
  }>();

  // ------------------------------------------------------------------
  // Popout manager (registers this window as "detached-tab" type)
  // ------------------------------------------------------------------
  const {
    playbackState,
    playerClosed,
    sendCommand,
    notifyReattachTab,
    unregister,
  } = usePopoutManager({
    windowType: "detached-tab",
    interactionId,
    tabId: tabId as TabId,
  });

  // ------------------------------------------------------------------
  // Data fetching
  // ------------------------------------------------------------------
  const { data: metadata, isLoading: metadataLoading } = useInteractionMetadata(
    customerId,
    interactionId,
  );

  const interactionType = (metadata?.interactionType as string) ?? "Audio";
  const hasMedia = MEDIA_TYPES.has(interactionType);
  const agentName = (metadata?.agentName as string) ?? "";

  const { data: transcript, isLoading: transcriptLoading } = useTranscript(
    customerId,
    interactionId,
  );

  const { data: summary, isLoading: summaryLoading } = useGetSummary(
    customerId,
    interactionId,
  );

  const { transcribeSummarizePhase, startTranscribeAndSummarize } =
    useHostedTranscribeAndSummarizeFlow(customerId, interactionId);

  // ------------------------------------------------------------------
  // beforeunload cleanup — fires before React unmount on hard closes
  // ------------------------------------------------------------------
  useEffect(() => {
    const handleBeforeUnload = () => unregister();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [unregister]);

  // ------------------------------------------------------------------
  // Document title
  // ------------------------------------------------------------------
  const tabLabel = getTabLabel(tabId);

  useEffect(() => {
    if (agentName) {
      document.title = `${tabLabel} \u2014 ${agentName}`;
    }
  }, [agentName, tabLabel]);

  // ------------------------------------------------------------------
  // Current time from playback state (for transcript highlighting)
  // ------------------------------------------------------------------
  const currentTimeSeconds = playbackState?.currentTime ?? 0;

  // ------------------------------------------------------------------
  // Mini controls: send commands via sendCommand
  // ------------------------------------------------------------------
  const handleCommand = useCallback(
    (cmd: Parameters<typeof sendCommand>[1]) => {
      sendCommand(interactionId, cmd);
    },
    [sendCommand, interactionId],
  );

  // ------------------------------------------------------------------
  // Close dialog
  // ------------------------------------------------------------------
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);

  const handleCloseButtonClick = useCallback(() => {
    setCloseDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    unregister();
    window.close();
  }, [unregister]);

  const handleDialogReturn = useCallback(() => {
    // "Return to player" — reattach the tab and close this window
    notifyReattachTab(interactionId, tabId as TabId);
    unregister();
    window.close();
  }, [interactionId, notifyReattachTab, tabId, unregister]);

  // ------------------------------------------------------------------
  // Loading state
  // ------------------------------------------------------------------
  if (metadataLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "var(--bg-surface)",
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "var(--bg-surface)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: "1px solid var(--border-subtle)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.2, color: "var(--text-primary)" }}
          >
            {tabLabel}
          </Typography>
          {agentName && (
            <Typography
              variant="caption"
              sx={{ color: "var(--text-secondary)", lineHeight: 1.2 }}
            >
              {agentName}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label="Close"
          size="small"
          onClick={handleCloseButtonClick}
          sx={{ flexShrink: 0, ml: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Mini playback controls */}
      <Box
        sx={{
          borderBottom: "1px solid var(--border-subtle)",
          flexShrink: 0,
        }}
      >
        <MiniPlaybackControls
          currentTime={playbackState?.currentTime ?? 0}
          duration={playbackState?.duration ?? 0}
          isPlaying={playbackState?.isPlaying ?? false}
          playbackRate={playbackState?.playbackRate ?? 1}
          playerClosed={playerClosed}
          onCommand={handleCommand}
        />
      </Box>

      {/* Tab content */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        <DetailTabContent
          activeTab={tabId as TabId}
          customerId={customerId}
          interactionId={interactionId}
          interactionType={interactionType}
          hasMedia={hasMedia}
          transcript={transcript}
          transcriptLoading={transcriptLoading}
          currentTimeSeconds={currentTimeSeconds}
          summary={summary}
          summaryLoading={summaryLoading}
          metadata={metadata ?? undefined}
          onTranscribeAndSummarize={startTranscribeAndSummarize}
          transcribeSummarizePhase={transcribeSummarizePhase}
        />
      </Box>

      {/* Close confirmation dialog */}
      <ClosePromptDialog
        open={closeDialogOpen}
        parentType="detached-tab"
        playerClosed={playerClosed}
        onReturn={handleDialogReturn}
        onClose={handleDialogClose}
      />
    </Box>
  );
}
